import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ChatPage from './pages/ChatPage';
import AdminPage from './pages/AdminPage';
import ConnectionsPage from './pages/ConnectionsPage';
import SearchInsightsPage from './pages/SearchInsightsPage';
import UsersPage from './pages/UsersPage';
import HistoryPage from './pages/HistoryPage';
import SettingsPage from './pages/SettingsPage';
import LoginPage from './pages/LoginPage';
import AuthCallback from './pages/AuthCallback';
import AppShell from './components/AppShell';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.title = 'AtCoastal.AI';
  }, []);

  if (loading) {
    return <div className="h-screen w-screen bg-[rgb(var(--bg-0))] flex items-center justify-center text-white/50">Loading...</div>;
  }

  return isAuthenticated ? children : <Navigate to="/login" replace state={{ from: location }} />;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <div className="h-screen w-screen bg-[rgb(var(--bg-0))] flex items-center justify-center text-white/50">Loading...</div>;
  }

  return user?.role === 'admin' ? children : <Navigate to="/chat" replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) { return <AuthCallback />; }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<Navigate to="/chat" replace />} />

      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <AppShell><ChatPage /></AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <AppShell><HistoryPage /></AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <AppShell><SettingsPage /></AppShell>
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AppShell><AdminPage /></AppShell>
          </AdminRoute>
        }
      />

      <Route
        path="/connections"
        element={
          <AdminRoute>
            <AppShell><ConnectionsPage /></AppShell>
          </AdminRoute>
        }
      />

      <Route
        path="/users"
        element={
          <AdminRoute>
            <AppShell><UsersPage /></AppShell>
          </AdminRoute>
        }
      />

      <Route
        path="/insights"
        element={
          <AdminRoute>
            <AppShell><SearchInsightsPage /></AppShell>
          </AdminRoute>
        }
      />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.documentElement.classList.add('dark');
    document.title = 'AtCoastal.AI';
  }, []);

  return (
    <Router>
      <AuthProvider>
        <Toaster position="top-right" theme="dark" />
        <AppRouter />
      </AuthProvider>
    </Router>
  );
}

export default App;
